import React, { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { ImmutableTrip } from 'src/types/trips/Trips';
import dateTimeDurationSelector from 'src/inbox/selectors/dateTimeDuration/dateTimeDuration';
import useOfferErrors from 'src/inbox/hooks/useOfferErrors';
import Card from '../../presentation/Card';
import { modified as tripModified } from '../../../helpers';
import { open } from '../../../../common/ducks/zippy';
import {
  FORMS,
  REQUESTED_CHANGES_WARNING,
  TIME_FIELDS,
  UNSENT_CHANGES_WARNING,
} from '../../../constants';
import { MOMENT_FORMAT } from '../../../../common/constants';
import DateTime from '../../DateTime';
import EditButton from '../../EditButton';
import DateTimeSnippet from '../../../../common/components/DateTimeSnippet';
import NotificationMessage from '../../../../common/components/NotificationMessage';
import cardStyles from '../../presentation/Card/Card.module.scss';
import styles from './TimeCard.module.scss';
import TimeModal from './modal';

// Import multiple css modules into a component
const s = {
  ...cardStyles,
  ...styles,
};

type TimeCardProps = {
  trip: ImmutableTrip;
  readOnly?: boolean;
  checkTripModification?: boolean;
  requestedChanges?: boolean;
};

const TimeCard: FC<TimeCardProps> = ({
  trip,
  readOnly,
  // TODO: change how this works
  checkTripModification,
  requestedChanges,
}) => {
  const {
    offerTrip: {
      hasPickupTime, startDateTimeMoment, endDateTimeMoment, isOfferTimesModified,
    },
  } = useSelector(dateTimeDurationSelector(trip.get('pk')));
  const { errors: { returnTime: invalidReturnTime } } = useOfferErrors(trip);

  const dispatch = useDispatch();
  const openModal = () => dispatch(open(FORMS.TIME));

  const tripIsModified = Boolean(checkTripModification && tripModified(trip, TIME_FIELDS));
  const modified = isOfferTimesModified || tripIsModified;

  return (
    <Card
      highlighted={modified}
      classNameModifier={!readOnly ? 'hoverEffect' : undefined}
      withoutMarginBottom
      withDashedBorderBottom
    >
      {!invalidReturnTime && isOfferTimesModified && (
        <NotificationMessage outdent={false}>
          {UNSENT_CHANGES_WARNING}
        </NotificationMessage>
      )}
      {!invalidReturnTime && requestedChanges && tripIsModified && !isOfferTimesModified && (
        <NotificationMessage outdent={false}>
          {REQUESTED_CHANGES_WARNING}
        </NotificationMessage>
      )}
      {invalidReturnTime && (
        <NotificationMessage outdent={false} variant="error">
          {'Return time must be later than departure time'}
        </NotificationMessage>
      )}
      {!readOnly && !hasPickupTime && (
        <button
          type="button"
          onClick={openModal}
          className={s.suggest}
        >
          <NotificationMessage
            outdent={false}
          >
            Please suggest depart time to send an offer.
          </NotificationMessage>
        </button>
      )}
      <DateTimeSnippet>
        <DateTime
          formatString={MOMENT_FORMAT.TIME}
          title={!hasPickupTime ? 'Departure and return times to be determined.' : ''}
          departDateTime={hasPickupTime ? startDateTimeMoment : undefined}
          returnDateTime={hasPickupTime ? endDateTimeMoment : undefined}
          classNameModifier={modified ? 'modified' : ''}
        />
      </DateTimeSnippet>
      {!readOnly && (
        <>
          <EditButton openModal={openModal} />
          <TimeModal trip={trip} />
        </>
      )}
    </Card>
  );
};

export default TimeCard;
